import {
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  ApplicationStore,
  getModuleGroupById,
  SOFTLINE_CONFIG_OFFLINE_FEATURE,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import {
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SoftlineEntityDetailService,
  SoftlineEntityService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import {
  provideSoftAppsModule,
  withLazySoftAppsModuleTranslation,
  withSoftAppsModuleInfo,
  withSoftAppsModuleRoutes,
} from '@softapps/core';
import { default as de } from '../i18n/de.json';
import { provideBestellerfassungGlobals } from './bestellerfassung.globals';
import { bestellerfassungRoutes } from './bestellerfassung.routes';
import {
  SOFTLINE_API_MDE_BESTELLERFASSUNG_ARTIKEL,
  SOFTLINE_API_MDE_BESTELLERFASSUNG_BEWEGUNGEN,
} from './bestellerfassung.api';
import {
  SOFTLINE_FEATURE_MDE_BESTELLERFASSUNG,
  SOFTLINE_FEATURE_MDE_BESTELLERFASSUNG_ARTIKEL_DETAILS,
  SOFTLINE_FEATURE_MDE_BESTELLERFASSUNG_BEWE,
} from './bestellerfassung.shared';
import { BestellerfassungResolver } from './resolver/bestellerfassung.resolver';
import { BestellerfassungService } from './services/bestellerfassung.service';
import { CheckEingabeService } from './services/check-eingabe.service';
import { BestellerfassungArtikelDetailStore } from './store/bestellerfassung-artikel-detail.store';
import { BestellerfassungBeweStore } from './store/bestellerfassung-bewe.store';
import { BestellerfassungStore } from './store/bestellerfassung.store';

export const bestellerfassungProviders = [
  provideEnvironmentInitializer(async () => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'mdeBestellerfassung',
      language: 'de',
      translations: de,
    });

    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: 'Bestellung',
      icon: 'fa-regular fa-cart-plus',
      route: '/mde-bestellung',
      permission: 'BESTELLUNG',
      group: getModuleGroupById(6),
    });

    store.commit(
      SOFTLINE_FEATURE_MDE_BESTELLERFASSUNG_BEWE,
      BestellerfassungBeweStore.mutations.setSort,
      {
        property: 'id',
        title: 'Erfassungszeitpunkt',
        direction: 'desc',
      } as any
    );
  }),
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_BESTELLERFASSUNG,
      feature: BestellerfassungStore.feature,
      providers: [
        {
          provide: BestellerfassungService,
          useClass: BestellerfassungService,
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_BESTELLERFASSUNG_BEWE,
      feature: BestellerfassungBeweStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () =>
            new SoftlineEntityService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_MDE_BESTELLERFASSUNG_BEWEGUNGEN
            ),
        },
      ],
    })
  ),
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_BESTELLERFASSUNG_ARTIKEL_DETAILS,
      feature: BestellerfassungArtikelDetailStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () => {
            return new SoftlineEntityDetailService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_MDE_BESTELLERFASSUNG_ARTIKEL,
              'detail'
            );
          },
        },
        {
          provide: CheckEingabeService,
          useClass: CheckEingabeService,
        },
      ],
    })
  ),
  BestellerfassungResolver,
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: { featureName: SOFTLINE_FEATURE_MDE_BESTELLERFASSUNG, fields: ['datum'] },
    multi: true,
  },
];

export function provideBestellerfassungModule() {
  return [
    provideSoftAppsModule(
      {
        name: 'mdeBestellerfassung',
        title: 'Bestellung',
        icon: 'fa-regular fa-cart-plus',
        route: '/mde-bestellung',
        permission: 'BESTELLUNG',
        group: getModuleGroupById(6),
      },
      withSoftAppsModuleRoutes({ app: bestellerfassungRoutes }),
      withLazySoftAppsModuleTranslation({
        lazy: { de: () => import('../i18n/de.json') },
      }),
      withSoftAppsModuleInfo({
        library: '@softapps/mde/bestellerfassung',
        description: 'Bestellungen mit dem MDE-Gerät erfassen.',
      })
    ),
    ...provideBestellerfassungGlobals(),
  ];
}
